/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getCurrentShipToState, getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import DynamicDropdown, { OptionObject } from "@insite/mobius/DynamicDropdown";
import { CustomAppState } from "@rc/App/Store/Reducers";
import { getDropShipAddressesState } from "@rc/App/Store/Selectors/CheckoutAddressesSelectors";
import debounce from "lodash/debounce";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    shipTosParameter: GetShipTosApiParameter;
    setShipTosParameter: (parameter: GetShipTosApiParameter) => void;
    onChange: (customerSequence: string) => void;
    shipTo: string | undefined;
    label: string;
}

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    const shipTosDataView = getShipTosDataView(state, props.shipTosParameter);

    return {
        shipTosDataView,
        dropShipState: getDropShipAddressesState(state),
    };
}

const mapDispatchToProps = {
    loadShipTos,

};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CurrentShippingAddressStyles {
}

// XNG-Changes: Update style
export const currentShippingAddressStyles: CurrentShippingAddressStyles = {
};


const MyAccountPaginatedShipTosDropdown: React.FunctionComponent<Props> = (props: Props) => {
    const {
        shipTosParameter,
        setShipTosParameter,
        onChange,
        shipTo,
        label,
        shipTosDataView,
        loadShipTos,
        dropShipState: { isLoading, data, useNewAddress },
    } = props;

    const [shipToSearchText, setShipToSearchText] = useState("");
    const [options, setOptions] = useState<OptionObject[]>([]);

    useEffect(() => {
        if (!shipTosDataView.isLoading) {
            loadShipTos(shipTosParameter);
        }
    }, [shipTosParameter]);

    useEffect(() => {
        if (shipTosDataView.value) {
            const options: OptionObject[] = shipTosDataView.value.map(billTo => ({
                optionText: billTo.label,
                optionValue: billTo.customerSequence,
            }));
            setOptions(options);
        }
    }, [shipTosDataView]);


    const searchTextChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        debouncedDoSearch(event.currentTarget.value);
    };

    const debouncedDoSearch = debounce((value: string) => {
        setShipToSearchText(value);
        setShipTosParameter({
            ...shipTosParameter,
            filter: value
        });
    }, 800);

    const onSelectionChange = (customerSequence?: string) => {
        onChange(customerSequence || "");
    }

    return (
        <DynamicDropdown
            {...{ isSearchDropdown: true }}
            onSelectionChange={onSelectionChange}
            onInputChange={searchTextChanged}
            selected={shipTo}
            label={label}
            isLoading={isLoading}
            options={options}
            search={shipToSearchText}
            data-test-selector="changeCustomerShipToSelector"
            filterOption={() => true}
        />
    );
};


export default connect(mapStateToProps, mapDispatchToProps)(MyAccountPaginatedShipTosDropdown);
